// Videos.js - YouTube-like video browser with custom video player

import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import axiosInstance from "./axiosInstance";
import VideoPlayer from "./components/VideoPlayer";
import "./styles/Videos.css";
import { AuthContext } from "./context/AuthContext";

const Videos = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const userRole = user?.role || null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Check if this is a public share link (has ?v= param and user not logged in)
  const videoParam = searchParams.get('v');
  const isPublicShareMode = videoParam && !isAuthenticated;
  const [editMode, setEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [showShareToast, setShowShareToast] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch videos function - reusable for refresh
  const fetchVideos = async (selectFirst = false) => {
    try {
      // Use plain axios for public endpoint to avoid 401 redirect issues
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await axios.get(`${API_BASE}/videos`);
      setVideos(response.data);

      // Check if there's a video specified in the URL
      const videoParam = searchParams.get('v');
      if (videoParam && response.data.length > 0) {
        // Support both clean URLs (hyphens) and original filenames (spaces)
        const normalizedParam = videoParam.replace(/-/g, ' ');
        const targetVideo = response.data.find(v =>
          v.filename === videoParam ||
          v.filename === normalizedParam ||
          v.filename.replace(/ /g, '-') === videoParam
        );
        if (targetVideo) {
          setSelectedVideo(targetVideo);
          return;
        }
      }

      // Default to first video if no valid parameter
      if (selectFirst && response.data.length > 0) {
        setSelectedVideo(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  // Fetch videos on component mount (public endpoint - no auth required)
  useEffect(() => {
    fetchVideos(true);
  }, [searchParams]);

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    // Update URL with clean format (hyphens instead of spaces)
    setSearchParams({ v: video.filename.replace(/ /g, '-') });
  };

  // Share button handler - copy link to clipboard
  const handleShare = () => {
    if (selectedVideo) {
      // Use clean URL-friendly slug (replace spaces with hyphens)
      const cleanFilename = selectedVideo.filename.replace(/ /g, '-');
      const shareUrl = `${window.location.origin}/videos?v=${cleanFilename}`;
      navigator.clipboard.writeText(shareUrl).then(() => {
        setShowShareToast(true);
        setTimeout(() => setShowShareToast(false), 2000);
      }).catch(err => {
        console.error('Failed to copy link:', err);
        // Fallback: show alert with URL
        alert(`Share link: ${shareUrl}`);
      });
    }
  };

  const handleFieldChange = (filename, field, value) => {
    setEditedFields((prev) => ({
      ...prev,
      [filename]: {
        ...prev[filename],
        [field]: value,
      },
    }));
  };

  const handleEditToggle = () => setEditMode(!editMode);

  const handleFilesAdded = (newFiles) => {
    const uniqueFiles = [...files, ...newFiles]
      .slice(0, 10) // Limit to 10 files
      .filter(
        (file, index, self) =>
          self.findIndex((f) => f.name === file.name) === index
      ); // Prevent duplicates
    setFiles(uniqueFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFilesAdded(droppedFiles);
  };

  const handleBrowseFiles = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFilesAdded(selectedFiles);
  };

  const handleFieldChangeForFiles = (index, field, value) => {
    const updatedFiles = [...files];
    updatedFiles[index][field] = value;
    setFiles(updatedFiles);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append("video", file); // Append the video file itself
      });

      // Append metadata as JSON for all files
      const metadata = files.map((file) => ({
        title: file.title || file.name, // Default to filename if title is not provided
        tags: file.tags || "",
        description: file.description || "",
        category: file.category || "",
      }));

      formData.append("metadata", JSON.stringify(metadata));

      const response = await axiosInstance.post("/videos/upload-video", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        },
      });

      if (response.data.success) {
        // Refresh video list from server
        await fetchVideos();
        // Close modal and reset
        setUploadModalOpen(false);
        setFiles([]);
        setUploadProgress(0);
      } else if (response.data.errors) {
        alert("Some videos failed to upload. Check console for details.");
        console.error("Upload Errors:", response.data.errors);
      }
    } catch (error) {
      console.error("Error uploading video(s):", error);
      alert("Failed to upload video(s). Please check your input or try again.");
    } finally {
      setIsUploading(false);
    }
  };

  
 const handleDelete = async (filename) => {
    try {
      await axiosInstance.delete(`/videos/${filename}`);
      setVideos(videos.filter((video) => video.filename !== filename));
    } catch (error) {
      console.error("Error deleting video:", error);
      alert("Failed to delete video.");
    }
  };

  // Save changes to video titles and descriptions
  const handleSaveChanges = async () => {
    try {
      const updates = Object.entries(editedFields).map(([filename, fields]) => ({
        filename,
        title: fields.title,
        description: fields.description,
      }));

      if (updates.length === 0) {
        alert("No changes to save.");
        return;
      }

      await axiosInstance.put("/videos/update-titles", { updates });

      // Update local state with the changes
      setVideos(videos.map(video => {
        const edited = editedFields[video.filename];
        if (edited) {
          return {
            ...video,
            title: edited.title || video.title,
            description: edited.description || video.description,
          };
        }
        return video;
      }));

      setEditedFields({});
      setEditMode(false);
      alert("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
      alert("Failed to save changes.");
    }
  };

  // Public share mode - minimal full-screen view with just the video
  if (isPublicShareMode) {
    return (
      <div className="public-video-container">
        {selectedVideo ? (
          <>
            <h2 className="public-video-title">{selectedVideo.title}</h2>
            <div className="public-video-player">
              <VideoPlayer
                key={selectedVideo.filename}
                src={selectedVideo.videoUrl}
                poster={selectedVideo.thumbnailUrl}
                spriteUrl={selectedVideo.spriteUrl}
                spriteVttUrl={selectedVideo.spriteVttUrl}
                videoId={selectedVideo.filename}
              />
            </div>
            <div className="main-video-actions">
              <a
                href={selectedVideo.downloadUrl}
                className="download-button"
                title="Download video"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 12l-4-4h2.5V2h3v6H12L8 12z"/>
                  <path d="M2 14h12v1H2z"/>
                </svg>
                Download
              </a>
            </div>
            <p className="public-video-description">
              {selectedVideo.description || ""}
            </p>
          </>
        ) : (
          <div className="public-video-loading">Loading video...</div>
        )}
      </div>
    );
  }

  return (
    <div className="videos-page-container">
      <div className="videos-main-area">
        {selectedVideo && (
          <>
            <h3 className="main-video-title">{selectedVideo.title}</h3>

            <div className="main-video-player">
              <VideoPlayer
                key={selectedVideo.filename}
                src={selectedVideo.videoUrl}
                poster={selectedVideo.thumbnailUrl}
                spriteUrl={selectedVideo.spriteUrl}
                spriteVttUrl={selectedVideo.spriteVttUrl}
                videoId={selectedVideo.filename}
                onShare={handleShare}
              />
            </div>
            <div className="main-video-actions">
              <a
                href={selectedVideo.downloadUrl}
                className="download-button"
                title="Download video"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 12l-4-4h2.5V2h3v6H12L8 12z"/>
                  <path d="M2 14h12v1H2z"/>
                </svg>
                Download
              </a>
            </div>
            <p className="main-video-description">
              <strong>Description:</strong>{" "}
              {selectedVideo.description || "No description available."}
            </p>
          </>
        )}
      </div>

      <div className="videos-container">
        <h2>Videos</h2>

        {(userRole === "admin" || userRole === "superadmin") && (
          <button
            className="edit-button"
            onClick={handleEditToggle}
          >
            {editMode ? "Cancel Edit" : "Edit Videos"}
          </button>
        )}

        <div className="video-grid-container">
          {videos.map((video) => (
            <div
              key={video.filename}
              className={`video-item ${
                selectedVideo?.filename === video.filename ? "selected" : ""
              }`}
              data-filename={video.filename}
              onClick={() => handleVideoClick(video)}
            >
              <div className="video-title">
                {editMode ? (
                  <input
                    type="text"
                    value={
                      editedFields[video.filename]?.title !== undefined
                        ? editedFields[video.filename].title
                        : video.title
                    }
                    onChange={(e) =>
                      handleFieldChange(video.filename, "title", e.target.value)
                    }
                    onClick={(e) => e.stopPropagation()}
                    className="edit-title-input"
                  />
                ) : (
                  video.title
                )}
              </div>
              <div
                className="video-thumbnail"
                style={{
                  backgroundImage: `url(${
                    video.thumbnailUrl || "path/to/default-thumbnail.jpg"
                  })`,
                }}
              ></div>
              <div className="video-description">
                {editMode ? (
                  <textarea
                    value={
                      editedFields[video.filename]?.description !== undefined
                        ? editedFields[video.filename].description
                        : (video.description || "")
                    }
                    onChange={(e) =>
                      handleFieldChange(
                        video.filename,
                        "description",
                        e.target.value
                      )
                    }
                    onClick={(e) => e.stopPropagation()}
                    className="edit-description-textarea"
                  />
                ) : (
                  video.description || "No description available."
                )}
              </div>

              {editMode && (
                <button
                  className="delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(video.filename);
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>

        {editMode && (
          <button className="save-changes-button" onClick={handleSaveChanges}>
            Save Changes
          </button>
        )}

        {(userRole === "admin" || userRole === "superadmin") && (
          <div className="upload-section">
            <button
              className="upload-button"
              onClick={() => setUploadModalOpen(true)}
            >
              Upload Videos
            </button>
          </div>
        )}
      </div>

      {/* Share toast notification */}
      {showShareToast && (
        <div className="share-toast">
          Link copied to clipboard!
        </div>
      )}

      {uploadModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Upload Videos</h2>
            <p>
              Drag and drop up to 10 video files here, or use the "Browse" button
              below.
            </p>

            <div
              className="drop-zone"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              Drag & Drop Files Here
            </div>
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/x-msvideo"
              multiple
              onChange={handleBrowseFiles}
              className="browse-input"
            />
            <div className="file-list">
              {files.map((file, index) => (
                <div key={file.name} className="file-item">
                  <strong>Filename:</strong> {file.name}
                  <div className="input-group">
                    <label>Title:</label>
                    <input
                      type="text"
                      value={file.title || ""}
                      onChange={(e) =>
                        handleFieldChangeForFiles(index, "title", e.target.value)
                      }
                    />
                  </div>
                  <div className="input-group">
                    <label>Tags:</label>
                    <input
                      type="text"
                      value={file.tags || ""}
                      onChange={(e) =>
                        handleFieldChangeForFiles(index, "tags", e.target.value)
                      }
                    />
                  </div>
                  <div className="input-group">
                    <label>Description:</label>
                    <textarea
                      value={file.description || ""}
                      onChange={(e) =>
                        handleFieldChangeForFiles(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="input-group">
                    <label>Category:</label>
                    <input
                      type="text"
                      value={file.category || ""}
                      onChange={(e) =>
                        handleFieldChangeForFiles(
                          index,
                          "category",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              ))}
            </div>

            {isUploading && (
              <div className="upload-progress-container">
                <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                <span className="upload-progress-text">{uploadProgress}%</span>
              </div>
            )}

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setUploadModalOpen(false)}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                className="upload-button"
                onClick={handleUpload}
                disabled={files.length === 0 || isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Videos;
